import type {
  ReactiveDropdownDiagnostics,
  ReactiveFieldLoadState,
} from '../../interfaces/reactive-field-runtime-state.interface';

export class ReactiveDropdownStateStore {

  private readonly loadKeys = new Set<string>();
  private readonly activeLoadKeys = new Map<string, string>();
  private readonly loadVersions = new Map<string, number>();
  private readonly resolvedLoadKeys = new Map<string, number>();
  private readonly fieldLoadStates = new Map<string, ReactiveFieldLoadState>();
  private readonly fieldErrors = new Map<string, string>();
  private readonly fieldWarnings = new Map<string, string>();
  private readonly fieldDiagnostics = new Map<string, ReactiveDropdownDiagnostics>();

  public clear(): void {
    this.loadKeys.clear();
    this.activeLoadKeys.clear();
    this.loadVersions.clear();
    this.resolvedLoadKeys.clear();
    this.fieldLoadStates.clear();
    this.fieldErrors.clear();
    this.fieldWarnings.clear();
    this.fieldDiagnostics.clear();
  }

  public getFieldLoadStates(): ReadonlyMap<string, ReactiveFieldLoadState> {
    return this.fieldLoadStates;
  }

  public getFieldErrors(): ReadonlyMap<string, string> {
    return this.fieldErrors;
  }

  public getFieldWarnings(): ReadonlyMap<string, string> {
    return this.fieldWarnings;
  }

  public getFieldDiagnostics(): ReadonlyMap<string, ReactiveDropdownDiagnostics> {
    return this.fieldDiagnostics;
  }

  public setFieldLoadState(fieldName: string, loadState: ReactiveFieldLoadState, errorMessage: string | null = null): void {
    this.fieldLoadStates.set(fieldName, loadState);

    if (errorMessage) {
      this.fieldErrors.set(fieldName, errorMessage);
    } else {
      this.fieldErrors.delete(fieldName);
    }

    if (loadState === 'loading' || loadState === 'idle' || loadState === 'error') {
      this.fieldWarnings.delete(fieldName);
    }
  }

  public setFieldWarning(fieldName: string, warningMessage: string | null): void {
    if (warningMessage !== null && warningMessage.trim() !== '') {
      this.fieldWarnings.set(fieldName, warningMessage);
    } else {
      this.fieldWarnings.delete(fieldName);
    }
  }

  public getFieldWarning(fieldName: string): string | null {
    return this.fieldWarnings.get(fieldName) ?? null;
  }

  public setFieldDiagnostics(fieldName: string, diagnostics: ReactiveDropdownDiagnostics): void {
    this.fieldDiagnostics.set(fieldName, diagnostics);
  }

  public addLoadKey(loadKey: string): void {
    this.loadKeys.add(loadKey);
  }

  public deleteLoadKey(loadKey: string): void {
    this.loadKeys.delete(loadKey);
  }

  public markLoadKeyResolved(loadKey: string, resolvedAt: number): void {
    this.resolvedLoadKeys.set(loadKey, resolvedAt);
  }

  public wasLoadKeyRecentlyResolved(loadKey: string, now: number, windowMs: number): boolean {
    const resolvedAt = this.resolvedLoadKeys.get(loadKey);

    if (resolvedAt === undefined) {
      return false;
    }

    const ageMs = now - resolvedAt;

    if (ageMs >= 0 && ageMs <= windowMs) {
      return true;
    }

    this.resolvedLoadKeys.delete(loadKey);

    return false;
  }

  public getActiveLoadKey(fieldName: string): string | null {
    return this.activeLoadKeys.get(fieldName) ?? null;
  }

  public setActiveLoadKey(fieldName: string, loadKey: string): void {
    this.activeLoadKeys.set(fieldName, loadKey);
  }

  public clearActiveLoadKey(fieldName: string): void {
    this.activeLoadKeys.delete(fieldName);
  }

  public nextLoadVersion(fieldName: string): number {
    const nextVersion = (this.loadVersions.get(fieldName) ?? 0) + 1;

    this.loadVersions.set(fieldName, nextVersion);

    return nextVersion;
  }

  public isLoadCurrent(fieldName: string, loadVersion: number, isDestroyed: boolean): boolean {
    return !isDestroyed && this.loadVersions.get(fieldName) === loadVersion;
  }
}
